import type { Message } from '../types';

// Phones often pause or reload a tab while someone is in another app. To get the
// chat back after a reload, the open chat with a person is kept in this tab's
// sessionStorage: only on this device, gone when the tab closes or the chat ends.
// Photos and voice notes are never kept; a short label takes their place.

const KEY = 'zuzu_open_chat';
// Same as the server's wait for someone to come back (CHAT_GRACE_MS).
export const CHAT_GRACE_MS = 5 * 60 * 1000;

export interface OpenChat {
  roomId: string;
  role: 'VENT' | 'LISTEN';
  otherName: string;
  partnerRole: 'VENT' | 'LISTEN' | null;
  messages: Message[];
  savedAt: number;
}

const keepable = (m: Message): Message => {
  if (m.type === 'text') return m;
  // A GIF is only a short link; a photo or voice note is never kept.
  if (m.type === 'image' && m.url && m.url.startsWith('http') && m.url.length < 2000) return m;
  const label = m.type === 'image' ? '🖼️ Photo' : '🎤 Voice note';
  // A photo or voice note still on its way can't be sent again after a reload.
  return { ...m, type: 'text', url: undefined, text: label, status: m.status === 'sending' ? 'failed' : m.status };
};

export function saveOpenChat(chat: Omit<OpenChat, 'savedAt'>) {
  try {
    const messages = chat.messages.slice(-200).map(keepable);
    sessionStorage.setItem(KEY, JSON.stringify({ ...chat, messages, savedAt: Date.now() }));
  } catch { /* storage full or blocked: the chat just won't survive a reload */ }
}

export function clearOpenChat() {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}

// The chat this tab had open, if it is recent enough to rejoin.
export function loadOpenChat(): OpenChat | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const chat = JSON.parse(raw) as OpenChat;
    if (!chat?.roomId || (chat.role !== 'VENT' && chat.role !== 'LISTEN') || Date.now() - chat.savedAt > CHAT_GRACE_MS) {
      clearOpenChat();
      return null;
    }
    // Texts still "Sending…" are sent again on rejoin (the server drops repeats).
    chat.messages = chat.messages ?? [];
    return chat;
  } catch {
    return null;
  }
}
